// ==========================================================================
// components/ContactPicker.tsx — "Who" box: type to find a contact, or add a
// new one on the spot (FF-042, 9 Oct 2026).
// ==========================================================================
import { useMemo, useState } from "react";
import { Plus, UserRound, X } from "lucide-react";
import { inputCls } from "@/components/Sheet";
import { useSaveContact, type Contact } from "@/lib/contacts";

export default function ContactPicker({ value, contacts, onChange, newName = "", newMatch = "", compact = false }: {
  value: string | null; contacts: Contact[]; onChange: (id: string | null, created?: Contact) => void;
  newName?: string; newMatch?: string; compact?: boolean;
}) {
  const save = useSaveContact();
  const [q, setQ] = useState(""); const [open, setOpen] = useState(false);
  const chosen = contacts.find((c) => c.id === value);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return contacts.filter((c) => !c.archived_at && (!s || c.name.toLowerCase().includes(s) || c.match_texts.some((m) => m.includes(s)))).slice(0, 30);
  }, [contacts, q]);
  const typed = (q.trim() || newName).trim();
  const exact = contacts.some((c) => !c.archived_at && c.name.toLowerCase() === typed.toLowerCase());

  if (!open) {
    return (
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => { setOpen(true); setQ(""); }} className={`${inputCls} ${compact ? "py-2 text-sm" : ""} flex items-center gap-2 text-left ${chosen ? "" : "text-ink-muted"}`}>
          <UserRound className="h-4 w-4 shrink-0 text-ink-muted" />
          <span className="truncate">{chosen ? chosen.name : "Who? (optional)"}</span>
        </button>
        {chosen && <button type="button" aria-label="Clear who" onClick={() => onChange(null)} className="text-ink-muted hover:text-ink"><X className="h-4 w-4" /></button>}
      </div>
    );
  }
  return (
    <div className="rounded-md border border-line bg-bg">
      <div className="flex items-center gap-2 border-b border-line px-3"><UserRound className="h-4 w-4 text-ink-muted" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={newName ? `Type a name, e.g. ${newName}` : "Type a name"} className="w-full bg-transparent py-2 text-sm outline-none" />
        <button type="button" aria-label="Close" onClick={() => setOpen(false)} className="text-ink-muted"><X className="h-4 w-4" /></button>
      </div>
      <div className="max-h-48 overflow-y-auto py-1">
        {list.map((c) => (
          <button key={c.id} type="button" onClick={() => { onChange(c.id); setOpen(false); }} className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-primary/[0.06] ${c.id === value ? "bg-primary/10" : ""}`}>
            <span className="flex-1 truncate">{c.name}</span><span className="text-xs capitalize text-ink-muted">{c.kind}</span>
          </button>
        ))}
        {typed && !exact && (
          <button type="button" disabled={save.isPending} onClick={async () => {
            const name = typed === typed.toUpperCase() ? typed.toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase()) : typed;
            const c: Contact = { id: "", name, kind: "business", person_id: null, match_texts: [...new Set([name.toLowerCase(), newMatch].filter((m) => m && m.length >= 3))], default_category_id: null, default_tracker_id: null, notes: null, archived_at: null };
            const id = await save.mutateAsync({ c: { name: c.name, kind: c.kind, match_texts: c.match_texts } });
            onChange(id, { ...c, id }); setOpen(false);
          }} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-primary hover:bg-primary/[0.06]">
            <Plus className="h-4 w-4" /> Add “{typed}” as a contact
          </button>
        )}
        {save.error && <p className="px-3 py-1 text-xs text-bad">{(save.error as Error).message}</p>}
      </div>
    </div>
  );
}
